import {Box, Colors, Text} from "@bounce/shared/ui";
import {ReachTemplateRenderResponse} from "../../../apis/requests/templates/types";
import {CONTENT_CHANNEL_LABELS, FormValues, MESSAGING_CHANNELS, MMS_CHANNELS} from "../templateFormSchema";
import EmailView from "./EmailView";
import HtmlFrame from "./HtmlFrame";

export type PreviewMode = "mobile" | "desktop";

const MMS_PREVIEW_WIDTH = 220;

type PreviewContentProps = {
  contentChannel: FormValues["content_channel"];
  renderResult: ReachTemplateRenderResponse | undefined;
  previewMode: PreviewMode;
};

const PreviewContent = ({contentChannel, renderResult, previewMode}: PreviewContentProps) => {
  if (!renderResult) {
    if (!contentChannel) return <Placeholder text="Pick a content channel to preview" />;
    return <Placeholder text="Pick a user to preview as" />;
  }

  const effectiveChannel = renderResult.content_channel;
  const effectiveBody = renderResult.rendered_body ?? "";
  const effectiveSubject = renderResult.rendered_subject ?? "";

  if (effectiveChannel === "email") {
    if (!effectiveBody.trim()) return <Placeholder text="Template has no HTML body" />;
    return (
      <EmailView
        subject={effectiveSubject}
        preHeader={renderResult.rendered_pre_header ?? ""}
        body={effectiveBody}
        compact={previewMode === "mobile"}
      />
    );
  }

  if (MESSAGING_CHANNELS.has(effectiveChannel)) {
    const imageHtml = MMS_CHANNELS.has(effectiveChannel) ? effectiveBody.trim() : "";
    if (!effectiveSubject.trim() && !imageHtml) return <Placeholder text="Template has no message" />;
    return <MessageBubble channel={effectiveChannel} message={effectiveSubject} imageHtml={imageHtml} />;
  }

  return <Placeholder text="Preview not supported for this channel yet" />;
};

const Placeholder = ({text}: {text: string}) => (
  <Box
    sx={{
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      px: 3,
    }}
  >
    <Text size={13} sx={{color: Colors.grey._500, textAlign: "center"}}>
      {text}
    </Text>
  </Box>
);

const MessageBubble = ({channel, message, imageHtml}: {channel: string; message: string; imageHtml?: string}) => (
  <Box sx={{p: 2, display: "flex", flexDirection: "column", gap: 1.5}}>
    <Text size={11} sx={{color: Colors.grey._600, textTransform: "uppercase", letterSpacing: 0.5}}>
      {CONTENT_CHANNEL_LABELS[channel as keyof typeof CONTENT_CHANNEL_LABELS] ?? channel}
    </Text>
    {imageHtml && (
      <Box
        sx={{
          alignSelf: "flex-start",
          border: `1px solid ${Colors.grey._200}`,
          borderRadius: "16px",
          overflow: "hidden",
        }}
      >
        <HtmlFrame html={imageHtml} title="MMS image preview" fitToWidth={MMS_PREVIEW_WIDTH} />
      </Box>
    )}
    {message.trim() && (
      <Box
        sx={{
          alignSelf: "flex-start",
          maxWidth: "85%",
          backgroundColor: Colors.bw.white,
          border: `1px solid ${Colors.grey._200}`,
          borderRadius: "16px",
          padding: "10px 14px",
        }}
      >
        <Text
          size={14}
          sx={{
            color: Colors.grey._900,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            lineHeight: 1.4,
          }}
        >
          {message}
        </Text>
      </Box>
    )}
    <Text size={11} sx={{color: Colors.grey._500, mt: 0.5}}>
      From: Bounce Recovery
    </Text>
  </Box>
);

export default PreviewContent;
